import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Select } from "../ui/select";
import { Separator } from "../ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Users, Building2, Tag, ArrowRightLeft, CheckCircle, X, AlertTriangle, Loader2, Trash2, Globe, MapPin, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../../store/slices/leadStatusSlice";
import { fetchDesks } from "../../services/crmServices/deskService";
import { searchUsers } from "../../services/crmServices/userService";
import { bulkUpdateLeads, bulkDeleteLeads as apiBulkDeleteLeads, fetchDistinctValues, bulkCleanLeadHistory } from "../../services/crmServices/leadService";
import { getFullName } from "../../services/common/util";
import { useAuth } from "../../hooks/useAuth";

export function BulkEditModal({ open, onClose, selectedLeads, onSuccess }) {
    const { user } = useAuth();
    const isAdmin = user?.position?.role === "admin";
    const leadStatuses = useSelector(selectLeadStatuses);
    const [loading, setLoading] = useState(false);
    const [desks, setDesks] = useState([]);
    const [agents, setAgents] = useState([]);
    const [selectedDesk, setSelectedDesk] = useState("");
    const [sources, setSources] = useState([]);
    // Progress tracking state for all operations
    const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });
    const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0, percentage: 0 });

    const [bulkActions, setBulkActions] = useState({
        changeStatus: false,
        transferDesk: false,
        changeSource: false,
        deleteLeads: false,
        cleanHistory: false
    });

    const [actionValues, setActionValues] = useState({
        newStatus: "",
        newAgent: "",
        newDesk: "",
        newSource: ""
    });

    // Sub-options for the Clean Lead History action (admin only).
    const [cleanOptions, setCleanOptions] = useState({
        clearComments: true,
        clearCallbacks: true,
        clearCallLogs: true,
        clearAssignmentHistory: true,
        clearConversionFlags: true,
        resetStatusToNew: true,
        resetCreatedAt: true,
        alsoUnassign: false
    });

    // Inline confirmation state for Clean Lead History.
    // Avoids using SweetAlert here because its input fights with Radix Dialog's
    // focus trap, making the textbox initially uneditable.
    const [showCleanConfirm, setShowCleanConfirm] = useState(false);
    const [cleanConfirmText, setCleanConfirmText] = useState("");

    useEffect(() => {
        if (open) {
            // Fetch desks
            fetchDesks().then((res) => {
                if (res?.data?.length) {
                    setDesks(res.data);
                }
            });
            
            // Fetch distinct sources
            fetchDistinctValues('source').then((res) => {
                if (res?.data) {
                    setSources(res.data.map((source) => ({ label: source, value: source })));
                }
            });
        }
    }, [open]);

    useEffect(() => {
        if (selectedDesk) {
            // Fetch agents for selected desk
            searchUsers({ agentDesk: selectedDesk }).then((res) => {
                if (res?.data) {
                    setAgents(res.data);
                } else {
                    setAgents([]);
                }
            });
        } else {
            setAgents([]);
        }
    }, [selectedDesk]);

    const handleActionToggle = (action) => {
        // Exclusive actions: enabling one disables all others.
        const exclusiveActions = ["deleteLeads", "cleanHistory"];

        if (exclusiveActions.includes(action) && !bulkActions[action]) {
            setBulkActions({
                changeStatus: false,
                transferDesk: false,
                changeSource: false,
                deleteLeads: false,
                cleanHistory: false,
                [action]: true
            });
            setActionValues({
                newStatus: "",
                newAgent: "",
                newDesk: "",
                newSource: ""
            });
            setSelectedDesk("");
            return;
        }

        // If enabling a non-exclusive action while an exclusive one is on, turn the exclusive off.
        const someExclusiveOn = exclusiveActions.some((a) => bulkActions[a]);
        if (!exclusiveActions.includes(action) && someExclusiveOn) {
            setBulkActions((prev) => ({
                ...prev,
                deleteLeads: false,
                cleanHistory: false,
                [action]: !prev[action]
            }));
            return;
        }

        setBulkActions((prev) => ({
            ...prev,
            [action]: !prev[action]
        }));

        // Clear the corresponding value when disabling an action
        if (bulkActions[action]) {
            let valueKey = "";
            if (action === "changeStatus") {
                valueKey = "newStatus";
            } else if (action === "transferDesk") {
                valueKey = "newDesk";
            } else if (action === "changeSource") {
                valueKey = "newSource";
            }

            setActionValues((prev) => ({
                ...prev,
                ...(valueKey && { [valueKey]: "" }),
                // Also clear agent when disabling transferDesk
                ...(action === "transferDesk" && { newAgent: "" })
            }));

            if (action === "transferDesk") {
                setSelectedDesk("");
            }
        }
    };

    const handleValueChange = (key, value) => {
        setActionValues((prev) => ({
            ...prev,
            [key]: value
        }));

        // If selecting a desk for transfer, update selectedDesk to load agents
        if (key === "newDesk") {
            setSelectedDesk(value);
        }
    };

    const validateActions = () => {
        // Exclusive actions skip the per-field validations.
        if (bulkActions.deleteLeads) return true;
        if (bulkActions.cleanHistory) {
            const anySubOption = Object.values(cleanOptions).some(Boolean);
            if (!anySubOption) {
                toast.error("Pick at least one history field to clean");
                return false;
            }
            return true;
        }

        if (!bulkActions.changeStatus && !bulkActions.transferDesk && !bulkActions.changeSource) {
            toast.error("Please select at least one action to perform");
            return false;
        }

        if (bulkActions.changeStatus && !actionValues.newStatus) {
            toast.error("Please select a status to change to");
            return false;
        }

        if (bulkActions.transferDesk && !actionValues.newDesk) {
            toast.error("Please select a desk to transfer to");
            return false;
        }

        if (bulkActions.changeSource && !actionValues.newSource) {
            toast.error("Please enter or select a source name");
            return false;
        }

        return true;
    };

    const handleBulkDelete = async () => {
        // Validate maximum delete limit (matches backend MAX_DELETE_BATCH_SIZE)
        const MAX_DELETE_LIMIT = 15000;
        if (selectedLeads.length > MAX_DELETE_LIMIT) {
            toast.error(`Cannot delete more than ${MAX_DELETE_LIMIT} leads at once. Please select fewer leads or split into smaller batches.`);
            return;
        }

        setLoading(true);
        const totalLeads = selectedLeads.length;
        setDeleteProgress({ current: 0, total: totalLeads, percentage: 0 });

        const progressToast = toast.loading(`Deleting ${totalLeads.toLocaleString()} lead(s)...`);
        const startTime = Date.now();

        // Indeterminate progress simulation - a single backend call deletes the whole batch
        // and produces ONE management-action log entry (instead of one per lead).
        let step = 0;
        const interval = setInterval(() => {
            step++;
            const pct = Math.min(95, Math.round(100 * (1 - Math.exp(-step / 8))));
            const current = Math.round((pct / 100) * totalLeads);
            setDeleteProgress({ current, total: totalLeads, percentage: pct });
            toast.loading(
                `Deleting ${current.toLocaleString()} / ${totalLeads.toLocaleString()} lead(s) (${pct}%)...`,
                { id: progressToast }
            );
        }, 300);

        try {
            const response = await apiBulkDeleteLeads(selectedLeads.map((l) => l._id));

            clearInterval(interval);
            toast.dismiss(progressToast);
            setDeleteProgress({ current: totalLeads, total: totalLeads, percentage: 100 });

            if (response?.status === 200) {
                const deleted = response?.data?.deletedCount ?? totalLeads;
                const elapsed = Math.round((Date.now() - startTime) / 1000);
                const timeText = elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
                toast.success(`Deleted ${deleted.toLocaleString()} lead(s) in ${timeText}`, { duration: 5000 });
                onSuccess();
                handleClose();
            } else {
                toast.error(response?.data?.message || "Failed to delete leads");
            }
        } catch (error) {
            clearInterval(interval);
            toast.dismiss(progressToast);
            console.error("Bulk delete error:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to delete leads";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
            setDeleteProgress({ current: 0, total: 0, percentage: 0 });
        }
    };

    const handleBulkClean = async () => {
        const MAX_CLEAN_LIMIT = 30000;
        if (selectedLeads.length > MAX_CLEAN_LIMIT) {
            toast.error(`Cannot clean more than ${MAX_CLEAN_LIMIT.toLocaleString()} leads at once. Please select fewer leads.`);
            return;
        }

        setLoading(true);
        const total = selectedLeads.length;
        setProgress({ current: 0, total, percentage: 0 });

        const progressToast = toast.loading(`Cleaning lead history... 0 / ${total.toLocaleString()}`);
        const startTime = Date.now();

        // Indeterminate progress simulation while the backend processes.
        let step = 0;
        const interval = setInterval(() => {
            step++;
            const pct = Math.min(95, Math.round(100 * (1 - Math.exp(-step / 8))));
            const current = Math.round((pct / 100) * total);
            setProgress({ current, total, percentage: pct });
            toast.loading(
                `Cleaning lead history... ${current.toLocaleString()} / ${total.toLocaleString()} (${pct}%)`,
                { id: progressToast }
            );
        }, 300);

        try {
            const response = await bulkCleanLeadHistory({
                leadIds: selectedLeads.map((l) => l._id),
                options: cleanOptions
            });

            clearInterval(interval);
            toast.dismiss(progressToast);
            setProgress({ current: total, total, percentage: 100 });

            if (response?.status === 200) {
                const modified = response?.data?.modifiedCount ?? total;
                const elapsed = Math.round((Date.now() - startTime) / 1000);
                const timeText = elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
                toast.success(`Cleaned history for ${modified.toLocaleString()} lead(s) in ${timeText}`, { duration: 5000 });
                onSuccess();
                handleClose();
            } else {
                toast.error(response?.data?.message || "Failed to clean lead history");
            }
        } catch (error) {
            clearInterval(interval);
            toast.dismiss(progressToast);
            console.error("Bulk clean error:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to clean lead history";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
            setProgress({ current: 0, total: 0, percentage: 0 });
        }
    };

    const handleBulkUpdate = async () => {
        if (!validateActions()) return;

        // If cleaning lead history, show inline confirmation panel.
        if (bulkActions.cleanHistory) {
            setCleanConfirmText("");
            setShowCleanConfirm(true);
            return;
        }

        // If deleting leads, show confirmation and delete
        if (bulkActions.deleteLeads) {
            Swal.fire({
                title: "Are you sure?",
                text: `You are about to delete ${selectedLeads.length} lead(s). This action cannot be undone!`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete them!",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#dc2626",
                cancelButtonColor: "#6b7280"
            }).then((result) => {
                if (result.isConfirmed) {
                    handleBulkDelete();
                }
            });
            return;
        }

        setLoading(true);
        const startTime = Date.now();
        const totalLeads = selectedLeads.length;
        setProgress({ current: 0, total: totalLeads, percentage: 0 });

        // Show progress toast
        const progressToast = toast.loading(`Preparing to update ${totalLeads.toLocaleString()} leads...`);

        try {
            const updateData = {
                leadIds: selectedLeads.map((lead) => lead._id),
                updates: {}
            };

            if (bulkActions.changeStatus) {
                updateData.updates.status = actionValues.newStatus;
            }

            if (bulkActions.transferDesk) {
                updateData.updates.desk = actionValues.newDesk;
                // If agent is selected, also update agent
                if (actionValues.newAgent) {
                    updateData.updates.agent = actionValues.newAgent;
                }
            }

            if (bulkActions.changeSource) {
                updateData.updates.source = actionValues.newSource;
            }

            // Enhanced progress simulation
            let progressStep = 0;
            const progressInterval = setInterval(() => {
                setProgress(prev => {
                    progressStep++;
                    const progressValue = Math.min(95, Math.round(100 * (1 - Math.exp(-progressStep / 8))));
                    const currentCount = Math.round((progressValue / 100) * totalLeads);
                    const elapsed = Math.round((Date.now() - startTime) / 1000);
                    const estimated = progressValue > 5 ? Math.round((elapsed / progressValue) * 100) : 0;
                    const remaining = Math.max(0, estimated - elapsed);
                    
                    toast.loading(
                        `Updating ${currentCount.toLocaleString()} / ${totalLeads.toLocaleString()} leads (${progressValue}%) • ${remaining}s remaining`,
                        { id: progressToast }
                    );
                    return { current: currentCount, total: totalLeads, percentage: progressValue };
                });
            }, 300);

            const response = await bulkUpdateLeads(updateData);

            clearInterval(progressInterval);
            const elapsed = Math.round((Date.now() - startTime) / 1000);
            toast.dismiss(progressToast);
            setProgress({ current: totalLeads, total: totalLeads, percentage: 100 });

            if (response?.status === 200) {
                const actualCount = response?.data?.count || selectedLeads.length;
                const timeText = elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
                toast.success(`✓ Successfully updated ${actualCount.toLocaleString()} leads in ${timeText}`, { duration: 5000 });
                onSuccess();
                handleClose();
            } else {
                toast.error(response?.data?.message || "Failed to update leads");
            }
        } catch (error) {
            toast.dismiss(progressToast);
            console.error("Bulk update error:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to update leads";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
            setProgress({ current: 0, total: 0, percentage: 0 });
        }
    };

    const handleClose = () => {
        setBulkActions({
            changeStatus: false,
            transferDesk: false,
            changeSource: false,
            deleteLeads: false,
            cleanHistory: false
        });
        setActionValues({
            newStatus: "",
            newAgent: "",
            newDesk: "",
            newSource: ""
        });
        setSelectedDesk("");
        setDeleteProgress({ current: 0, total: 0, percentage: 0 });
        setShowCleanConfirm(false);
        setCleanConfirmText("");
        onClose();
    };

    const getActionSummary = () => {
        const actions = [];
        if (bulkActions.changeStatus) {
            const statusLabel = leadStatuses.find((s) => s.value === actionValues.newStatus)?.label;
            actions.push(`Change status to "${statusLabel}"`);
        }
        if (bulkActions.transferDesk) {
            const deskName = desks.find((d) => d._id === actionValues.newDesk)?.name;
            let transferAction = `Transfer to ${deskName} desk`;

            if (actionValues.newAgent) {
                const agentName = agents.find((a) => a._id === actionValues.newAgent);
                transferAction += ` and assign to ${getFullName(agentName)}`;
            }

            actions.push(transferAction);
        }
        if (bulkActions.changeSource) {
            actions.push(`Change source to "${actionValues.newSource}"`);
        }
        if (bulkActions.deleteLeads) {
            actions.push(`Delete ${selectedLeads.length} lead(s)`);
        }
        if (bulkActions.cleanHistory) {
            const parts = [];
            if (cleanOptions.clearComments) parts.push("comments");
            if (cleanOptions.clearCallbacks) parts.push("callbacks");
            if (cleanOptions.clearCallLogs) parts.push("call logs");
            if (cleanOptions.clearAssignmentHistory) parts.push("assignment history");
            if (cleanOptions.clearConversionFlags) parts.push("conversion flags");
            if (cleanOptions.resetStatusToNew) parts.push("status -> new");
            if (cleanOptions.resetCreatedAt) parts.push("reset age");
            if (cleanOptions.alsoUnassign) parts.push("unassign");
            actions.push(
                `Clean history of ${selectedLeads.length} lead(s) (${parts.length ? parts.join(", ") : "nothing selected"})`
            );
        }
        return actions;
    };

    const toggleCleanOption = (key) => {
        setCleanOptions((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
                <DialogHeader className="flex-shrink-0">
                    <DialogTitle className="flex items-center">
                        <Users className="h-5 w-5 mr-2" />
                        Bulk Edit Leads
                    </DialogTitle>
                    <DialogDescription>Apply changes to {selectedLeads.length} selected leads</DialogDescription>
                </DialogHeader>

                <div className="space-y-6 overflow-y-auto flex-1 pr-2 -mr-2">
                    {/* Bulk Actions */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium">Select Actions to Perform</h3>

                        {/* Change Status */}
                        <Card className={bulkActions.changeStatus ? "ring-2 ring-blue-500" : ""}>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="changeStatus"
                                            checked={bulkActions.changeStatus}
                                            onChange={() => handleActionToggle("changeStatus")}
                                            disabled={bulkActions.deleteLeads || bulkActions.cleanHistory}
                                            className="rounded"
                                        />
                                        <Label htmlFor="changeStatus" className="flex items-center cursor-pointer">
                                            <Tag className="h-4 w-4 mr-2" />
                                            Change Status
                                        </Label>
                                    </div>
                                </div>
                                {bulkActions.changeStatus && (
                                    <div>
                                        <Label>New Status</Label>
                                        <Select
                                            options={leadStatuses}
                                            value={actionValues.newStatus}
                                            onChange={(value) => handleValueChange("newStatus", value)}
                                            placeholder="Select new status"
                                            searchable
                                        />
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Assign to Desk */}
                        <Card className={bulkActions.transferDesk ? "ring-2 ring-purple-500" : ""}>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="transferDesk"
                                            checked={bulkActions.transferDesk}
                                            onChange={() => handleActionToggle("transferDesk")}
                                            disabled={bulkActions.deleteLeads || bulkActions.cleanHistory}
                                            className="rounded"
                                        />
                                        <Label htmlFor="transferDesk" className="flex items-center cursor-pointer">
                                            <Building2 className="h-4 w-4 mr-2" />
                                            Assign to Desk
                                        </Label>
                                    </div>
                                </div>
                                {bulkActions.transferDesk && (
                                    <div className="space-y-3">
                                        <div>
                                            <Label>Target Desk</Label>
                                            <Select
                                                options={desks.map((desk) => ({ label: desk.name, value: desk._id }))}
                                                value={actionValues.newDesk}
                                                onChange={(value) => handleValueChange("newDesk", value)}
                                                placeholder="Select target desk"
                                                searchable
                                            />
                                        </div>
                                        {actionValues.newDesk && agents.length > 0 && (
                                            <div>
                                                <Label>Assign to Agent (Optional)</Label>
                                                <Select
                                                    options={agents.map((agent) => ({
                                                        label: getFullName(agent),
                                                        value: agent._id
                                                    }))}
                                                    value={actionValues.newAgent}
                                                    onChange={(value) => handleValueChange("newAgent", value)}
                                                    placeholder="Select agent (optional)"
                                                    searchable
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Change Source */}
                        <Card className={bulkActions.changeSource ? "ring-2 ring-green-500" : ""}>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="changeSource"
                                            checked={bulkActions.changeSource}
                                            onChange={() => handleActionToggle("changeSource")}
                                            disabled={bulkActions.deleteLeads || bulkActions.cleanHistory}
                                            className="rounded"
                                        />
                                        <Label htmlFor="changeSource" className="flex items-center cursor-pointer">
                                            <Globe className="h-4 w-4 mr-2" />
                                            Change Source
                                        </Label>
                                    </div>
                                </div>
                                {bulkActions.changeSource && (
                                    <div>
                                        <Label>New Source Name</Label>
                                        <div className="space-y-2">
                                            <Input
                                                type="text"
                                                value={actionValues.newSource}
                                                onChange={(e) => handleValueChange("newSource", e.target.value)}
                                                placeholder="Enter source name"
                                                list="source-options"
                                            />
                                            {sources.length > 0 && (
                                                <datalist id="source-options">
                                                    {sources.map((source) => (
                                                        <option key={source.value} value={source.value} />
                                                    ))}
                                                </datalist>
                                            )}
                                        </div>
                                        {sources.length > 0 && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                Or select from existing: {sources.slice(0, 5).map(s => s.label).join(", ")}
                                                {sources.length > 5 && ` +${sources.length - 5} more`}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Delete Leads */}
                        {user.permissions?.leads?.delete && (
                            <Card className={bulkActions.deleteLeads ? "ring-2 ring-red-500 border-red-300" : ""}>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="deleteLeads"
                                            checked={bulkActions.deleteLeads}
                                            onChange={() => handleActionToggle("deleteLeads")}
                                            className="rounded"
                                        />
                                            <Label htmlFor="deleteLeads" className="flex items-center cursor-pointer text-red-600">
                                                <Trash2 className="h-4 w-4 mr-2" />
                                                Delete Leads
                                            </Label>
                                        </div>
                                    </div>
                                    {bulkActions.deleteLeads && (
                                        <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                                            <p className="text-sm text-red-800">
                                                <strong>Warning:</strong> This will permanently delete {selectedLeads.length} lead(s).
                                                This action cannot be undone!
                                            </p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Clean Lead History (admin only) */}
                        {isAdmin && (
                            <Card className={bulkActions.cleanHistory ? "ring-2 ring-purple-500 border-purple-300" : ""}>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="cleanHistory"
                                                checked={bulkActions.cleanHistory}
                                                onChange={() => handleActionToggle("cleanHistory")}
                                                className="rounded"
                                            />
                                            <Label htmlFor="cleanHistory" className="flex items-center cursor-pointer text-purple-700">
                                                <Sparkles className="h-4 w-4 mr-2" />
                                                Clean Lead History (Make Fresh)
                                            </Label>
                                        </div>
                                    </div>
                                    {bulkActions.cleanHistory && !showCleanConfirm && (
                                        <div className="space-y-3">
                                            <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                                                <p className="text-sm text-purple-800">
                                                    <strong>Heads up:</strong> Wipes the selected lead&apos;s history (calls, comments, callbacks, assignment history, conversion flags) and resets its age. The lead&apos;s contact data and current assignment are preserved unless you toggle &quot;Also unassign&quot; below. <strong>Cannot be undone.</strong> Audit trail is recorded on each lead (cleanedAt / cleanedBy).
                                                </p>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-1">
                                                <label className="flex items-center space-x-2 text-sm cursor-pointer">
                                                    <input type="checkbox" checked={cleanOptions.clearComments} onChange={() => toggleCleanOption("clearComments")} className="rounded" />
                                                    <span>Clear comments</span>
                                                </label>
                                                <label className="flex items-center space-x-2 text-sm cursor-pointer">
                                                    <input type="checkbox" checked={cleanOptions.clearCallbacks} onChange={() => toggleCleanOption("clearCallbacks")} className="rounded" />
                                                    <span>Clear callbacks</span>
                                                </label>
                                                <label className="flex items-center space-x-2 text-sm cursor-pointer">
                                                    <input type="checkbox" checked={cleanOptions.clearCallLogs} onChange={() => toggleCleanOption("clearCallLogs")} className="rounded" />
                                                    <span>Clear call logs (count + records)</span>
                                                </label>
                                                <label className="flex items-center space-x-2 text-sm cursor-pointer">
                                                    <input type="checkbox" checked={cleanOptions.clearAssignmentHistory} onChange={() => toggleCleanOption("clearAssignmentHistory")} className="rounded" />
                                                    <span>Clear assignment history</span>
                                                </label>
                                                <label className="flex items-center space-x-2 text-sm cursor-pointer">
                                                    <input type="checkbox" checked={cleanOptions.clearConversionFlags} onChange={() => toggleCleanOption("clearConversionFlags")} className="rounded" />
                                                    <span>Clear conversion flags</span>
                                                </label>
                                                <label className="flex items-center space-x-2 text-sm cursor-pointer">
                                                    <input type="checkbox" checked={cleanOptions.resetStatusToNew} onChange={() => toggleCleanOption("resetStatusToNew")} className="rounded" />
                                                    <span>Reset status to &quot;new&quot;</span>
                                                </label>
                                                <label className="flex items-center space-x-2 text-sm cursor-pointer">
                                                    <input type="checkbox" checked={cleanOptions.resetCreatedAt} onChange={() => toggleCleanOption("resetCreatedAt")} className="rounded" />
                                                    <span>Reset age (createdAt &rarr; now)</span>
                                                </label>
                                                <label className="flex items-center space-x-2 text-sm cursor-pointer text-red-700">
                                                    <input type="checkbox" checked={cleanOptions.alsoUnassign} onChange={() => toggleCleanOption("alsoUnassign")} className="rounded" />
                                                    <span>Also unassign (remove agent/desk/floorManager)</span>
                                                </label>
                                            </div>
                                            <p className="text-xs text-gray-500 pl-1">Limit: 30,000 leads per operation.</p>
                                        </div>
                                    )}

                                    {bulkActions.cleanHistory && showCleanConfirm && (
                                        <div className="space-y-3">
                                            <div className="bg-purple-100 p-4 rounded-lg border-2 border-purple-400">
                                                <div className="flex items-start gap-2">
                                                    <AlertTriangle className="h-5 w-5 text-purple-700 flex-shrink-0 mt-0.5" />
                                                    <div className="space-y-2">
                                                        <p className="text-sm text-purple-900 font-medium">
                                                            Confirm: Clean history of <strong>{selectedLeads.length.toLocaleString()}</strong> lead(s)?
                                                        </p>
                                                        <p className="text-sm text-purple-800">
                                                            Will erase: <i>{getActionSummary()[0]?.replace(/^Clean history of \d[^()]*\(([^)]*)\)$/, "$1") || "nothing selected"}</i>.
                                                        </p>
                                                        <p className="text-sm font-semibold text-red-700">This action cannot be undone.</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <Label htmlFor="cleanConfirmInput" className="text-sm">
                                                    Type <strong>CLEAN</strong> to confirm:
                                                </Label>
                                                <Input
                                                    id="cleanConfirmInput"
                                                    type="text"
                                                    value={cleanConfirmText}
                                                    onChange={(e) => setCleanConfirmText(e.target.value)}
                                                    placeholder="Type CLEAN to confirm"
                                                    autoFocus
                                                    autoComplete="off"
                                                    className="mt-1"
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" && cleanConfirmText === "CLEAN" && !loading) {
                                                            setShowCleanConfirm(false);
                                                            handleBulkClean();
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div className="flex gap-2">
                                                <Button
                                                    onClick={() => {
                                                        setShowCleanConfirm(false);
                                                        handleBulkClean();
                                                    }}
                                                    disabled={cleanConfirmText !== "CLEAN" || loading}
                                                    className="bg-purple-600 hover:bg-purple-700 flex-1"
                                                >
                                                    <Sparkles className="h-4 w-4 mr-2" />
                                                    Yes, clean history
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    onClick={() => {
                                                        setShowCleanConfirm(false);
                                                        setCleanConfirmText("");
                                                    }}
                                                    disabled={loading}
                                                    className="flex-1"
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Update Progress Bar */}
                    {loading && !bulkActions.deleteLeads && progress.total > 0 && (
                        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg">
                            <CardContent className="pt-4">
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center space-x-3">
                                            <div className="relative">
                                                <ArrowRightLeft className="h-5 w-5 text-blue-600 animate-spin" style={{ animationDuration: '2s' }} />
                                                <div className="absolute inset-0 bg-blue-400 rounded-full animate-ping opacity-20"></div>
                                            </div>
                                            <div>
                                                <span className="text-sm font-semibold text-blue-900 block">
                                                    Updating leads in progress
                                                </span>
                                                <span className="text-xs text-blue-600">
                                                    Please wait, processing {progress.total.toLocaleString()} leads...
                                                </span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-lg font-bold text-blue-700 block">
                                                {progress.percentage}%
                                            </span>
                                            <span className="text-xs text-blue-600">
                                                {progress.current.toLocaleString()} / {progress.total.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="w-full bg-blue-200 rounded-full h-3 shadow-inner overflow-hidden">
                                        <div
                                            className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500 ease-out relative overflow-hidden"
                                            style={{ width: `${progress.percentage}%` }}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Delete Progress Bar */}
                    {loading && bulkActions.deleteLeads && deleteProgress.total > 0 && (
                        <Card className="border-red-200 bg-gradient-to-r from-red-50 to-pink-50 shadow-lg">
                            <CardContent className="pt-4">
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center space-x-3">
                                            <div className="relative">
                                                <Trash2 className="h-5 w-5 text-red-600 animate-pulse" />
                                                <div className="absolute inset-0 bg-red-400 rounded-full animate-ping opacity-20"></div>
                                            </div>
                                            <div>
                                                <span className="text-sm font-semibold text-red-900 block">
                                                    Deleting leads...
                                                </span>
                                                <span className="text-xs text-red-600">
                                                    Processing {deleteProgress.total.toLocaleString()} leads...
                                                </span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-lg font-bold text-red-700 block">
                                                {deleteProgress.percentage}%
                                            </span>
                                            <span className="text-xs text-red-600">
                                                {deleteProgress.current.toLocaleString()} / {deleteProgress.total.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="w-full bg-red-200 rounded-full h-3 shadow-inner overflow-hidden">
                                        <div
                                            className="bg-gradient-to-r from-red-500 to-pink-600 h-3 rounded-full transition-all duration-500 ease-out"
                                            style={{ width: `${deleteProgress.percentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Action Summary */}
                    {getActionSummary().length > 0 && (
                        <Card className={bulkActions.deleteLeads ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}>
                            <CardHeader>
                                <CardTitle className={`text-lg ${bulkActions.deleteLeads ? "text-red-900" : "text-blue-900"}`}>
                                    Action Summary
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    <p className={`text-sm ${bulkActions.deleteLeads ? "text-red-800" : "text-blue-800"}`}>
                                        The following actions will be applied to{" "}
                                        <strong>{selectedLeads.length} leads</strong>:
                                    </p>
                                    <ul className={`list-disc list-inside text-sm space-y-1 ${bulkActions.deleteLeads ? "text-red-800" : "text-blue-800"}`}>
                                        {getActionSummary().map((action, index) => (
                                            <li key={index}>{action}</li>
                                        ))}
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Warning */}
                    {!bulkActions.deleteLeads && (
                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                            <div className="flex items-start">
                                <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                                <div>
                                    <h4 className="font-medium text-yellow-900 mb-2">Important Notes</h4>
                                    <ul className="text-sm text-yellow-800 space-y-1">
                                        <li>• This action cannot be undone</li>
                                        <li>• All selected leads will be updated simultaneously</li>
                                        <li>• Lead history will be preserved</li>
                                        <li>• Changes will be logged in the system</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                {/* Action Buttons - Sticky Footer */}
                <div className="flex space-x-3 pt-4 border-t bg-white flex-shrink-0 -mx-6 -mb-6 px-6 pb-6">
                    <Button
                        onClick={handleBulkUpdate}
                        disabled={loading || getActionSummary().length === 0 || showCleanConfirm}
                        className={`flex-1 ${
                            bulkActions.deleteLeads
                                ? "bg-red-600 hover:bg-red-700"
                                : bulkActions.cleanHistory
                                  ? "bg-purple-600 hover:bg-purple-700"
                                  : ""
                        }`}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                {bulkActions.deleteLeads
                                    ? "Deleting..."
                                    : bulkActions.cleanHistory
                                      ? "Cleaning..."
                                      : "Updating..."}
                            </>
                        ) : (
                            <>
                                {bulkActions.deleteLeads ? (
                                    <>
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Delete {selectedLeads.length} Leads
                                    </>
                                ) : bulkActions.cleanHistory ? (
                                    <>
                                        <Sparkles className="h-4 w-4 mr-2" />
                                        Clean History of {selectedLeads.length} Leads
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Apply Changes to {selectedLeads.length} Leads
                                    </>
                                )}
                            </>
                        )}
                    </Button>
                    <Button variant="outline" onClick={handleClose} disabled={loading} className="flex-1">
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
